import React, { useEffect, useState } from "react";
import { OverlayTrigger, Placeholder, Popover, Toast, ToastContainer, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import DstButton from "../../../../components/DstButton/DstButton";
import DstConfirm from "../../../../components/DstConfirm/DstConfirm";
import DstTable from "../../../../components/DstTable/DstTable";
import DstTableSearch from "../../../../components/DstTable/DstTableSearch/DstTableSearch";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import { formatMinutesHourly, getAgeHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { completeNavigate } from "../../../../services/UtilityService";
import AddModulesPopup from "./AddModulesPopup/AddModulesPopup";
import AddUsersToOtherCohortPopup from "./AddUsersToOtherCohortPopup/AddUsersToOtherCohortPopup";
import CohortPartnerDiplomaPopup from "./CohortPartnerDiplomaPopup/CohortPartnerDiplomaPopup";
import { fetchUsers, fetchUsersLogtime, getUserActive, styleUserRow, updateUsers } from "./CohortUsers.function";
import "./CohortUsers.scss";
import CohortUserEvaluationAttemptsPopup from "./CohortUsersEvaluationAttemptPopup/CohortUsersEvaluationAttemptPopup";
import { CohortUsersFlagsPopup } from "./CohortUsersFlagsPopup/CohortUsersFlagsPopup";
import CohortUsersPopup from "./CohortUsersPopup/CohortUsersPopup";
import { CohortUsersProgressReportPopup } from "./CohortUsersProgressReportPopup/CohortUsersProgressReportPopup";

const CohortUsers = ({
  cohortId,
  language,
  isCohortPartner,
  onCountLoaded,
  onUsersAdded,
  onUsersRemoved,
}: {
  cohortId: string;
  language: string;
  isCohortPartner: boolean;
  onCountLoaded?: (count: number) => void;
  onUsersAdded?: () => void;
  onUsersRemoved?: () => void;
}) => {
  const navigate = useNavigate();
  const [cohortUsers, setCohortUsers] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingLogtime, setLoadingLogtime] = useState(false);
  const [usersSelected, setUsersSelected] = useState<any[]>([]);
  const [openPartnerDiplomaModal, setOpenPartnerDiplomaModal] = useState(false);
  const [openAddUsersModal, setOpenAddUsersModal] = useState(false);
  const [openEvaluationInvitationModal, setOpenEvaluationInvitationModal] = useState(false);
  const [openAddUsersToOtherCohortModal, setOpenAddUsersToOtherCohortModal] = useState(false);
  const [openDownloadProgressReportModal, setOpenDownloadProgressReportModal] = useState(false);
  const [openAddFlagsToUsersModal, setOpenAddFlagsToUsersModal] = useState(false);
  const [openAddModuleModal, setOpenAddModuleModal] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showConfirmRemoveUsers, setShowConfirmRemoveUsers] = useState(false);

  const _copyAllEmails = () => {
    navigator.clipboard
      .writeText(cohortUsers.map((user) => user.email).join(", "))
      .then(() => setShowCopyToast(true))
      .catch((err) => console.error("Failed to copy emails:", err));
  };

  const columns = [
    { name: translate(language, "PAGES.COHORT.USERS.TABLE_HEADER.LAST_NAME"), key: "last_name" },
    {
      name: translate(language, "PAGES.COHORT.USERS.TABLE_HEADER.FIRST_NAME"),
      key: "first_name",
    },
    {
      name: translate(language, "PAGES.COHORT.USERS.TABLE_HEADER.BADGES"),
      key: "flags",
      render: (elem: any) => {
        const allFlags = [
          elem.has_extra_time && { icon_name: "clock-stopwatch", name: "Extra Time" },
          ...(elem.flags || []),
        ].filter(Boolean);

        if (allFlags.length === 0) {
          return "N/A";
        }

        const visibleFlags = allFlags.slice(0, 3);
        const remainingFlags = allFlags.slice(3);

        const popover = (
          <Popover id={`popover-flags-${elem.id}`}>
            <Popover.Body>
              {remainingFlags.map((flag, index) => (
                <div key={`flag-${index}`} className="d-flex align-items-center">
                  <span className={`dst-icon-${flag.icon_name} me-2 blue-font`} />
                  <span>{flag.name}</span>
                </div>
              ))}
            </Popover.Body>
          </Popover>
        );
        return (
          <span>
            {visibleFlags.map((flag, index) => (
              <OverlayTrigger
                key={`flag-${index}`}
                trigger={["hover", "focus"]}
                placement="top"
                overlay={<Tooltip id={`tooltip-${flag.name}`}>{flag.name}</Tooltip>}
              >
                <span className={`dst-icon-${flag.icon_name} me-2 blue-font`} />
              </OverlayTrigger>
            ))}
            {remainingFlags.length > 0 && (
              <OverlayTrigger trigger={["hover", "focus"]} placement="top" overlay={popover}>
                <span className="rounded light-blue-background blue-font p-1">+{remainingFlags.length}</span>
              </OverlayTrigger>
            )}
          </span>
        );
      },
    },
    { name: translate(language, "PAGES.COHORT.USERS.TABLE_HEADER.EMAIL"), key: "email" },
    {
      name: translate(language, "PAGES.COHORT.USERS.TABLE_HEADER.MANDATORY_EXAMS"),
      key: "validated_mandatory_exams",
      render: (elem: any) => elem.validated_mandatory_exams || "N/A",
    },
    {
      name: translate(language, "PAGES.COHORT.USERS.TABLE_HEADER.MODULES"),
      key: "validated_modules",
      render: (elem: any) => elem.validated_modules || "N/A",
    },
    {
      name: translate(language, "PAGES.COHORT.USERS.TABLE_HEADER.ACTIVE_TIME"),
      key: "total_time",
      render: (elem: any) =>
        elem.total_time !== undefined ? formatMinutesHourly(elem.total_time) : "Waiting to fetch",
    },
    {
      name: translate(language, "PAGES.COHORT.USERS.TABLE_HEADER.LAST_ACTIVITY"),
      key: "dt_last_seen",
      sortCompute: (elem: any) => {
        const time = new Date(elem.dt_last_seen).getTime();
        return Number.isFinite(time) ? time : -1;
      },
      render: (elem: any) =>
        elem.dt_last_seen ? getAgeHumanly(language, new Date(elem.dt_last_seen).getTime(), true) : "N/A",
    },
    {
      name: translate(language, "PAGES.COHORT.USERS.TABLE_HEADER.HAS_PROJECT_LINK"),
      key: "has_project_in_cohort",
      render: (elem: any) => (
        <div className="text-center">
          {elem.has_project_in_cohort ? (
            <span className="dst-icon-check green-font" />
          ) : (
            <span className="dst-icon-x dark-red-font" />
          )}
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.COHORT.USERS.TABLE_HEADER.ACTIVE"),
      key: "active",
      render: (elem: any) => getUserActive(elem, language),
    },
  ];
  const preProcessedData = cohortUsers.map((item: any) => ({ ...item, columns }));

  const _REMOVE_USER_NOTE_KEYS = [
    { id: "deployment_mistake", key: "PAGES.COHORT.USERS.REMOVE_CONFIRM.REASONS.DEPLOYMENT_MISTAKE" },
    { id: "learner_dropped", key: "PAGES.COHORT.USERS.REMOVE_CONFIRM.REASONS.LEARNER_DROPPED" },
    { id: "contract_breach", key: "PAGES.COHORT.USERS.REMOVE_CONFIRM.REASONS.CONTRACT_BREACH" },
    { id: "cohort_change", key: "PAGES.COHORT.USERS.REMOVE_CONFIRM.REASONS.COHORT_CHANGE" },
    { id: "other", key: "PAGES.COHORT.USERS.REMOVE_CONFIRM.REASONS.OTHER" },
  ];

  useEffect(() => {
    fetchUsers(
      cohortId,
      (users: any[]) => {
        setCohortUsers(users);
        onCountLoaded?.(users.length);
      },
      language,
      () => setLoading(false)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Placeholder animation="glow">
        <div className="mx-3">
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex">
              <Placeholder className="me-3 align-content-center mb-0" as="h2" xs={12} md={6} />
              <Placeholder as="input" xs={12} md={4} />
            </div>
            <DstButton mock btnWidth="100px" />
          </div>
          <Placeholder xs={12} className="mb-3" style={{ height: 20 }} />
          <Placeholder xs={12} className="mb-3" style={{ height: 20 }} />
          <Placeholder xs={12} className="mb-3" style={{ height: 20 }} />
          <Placeholder xs={12} className="mb-3" style={{ height: 20 }} />
        </div>
      </Placeholder>
    );
  }

  return (
    <div className="mx-3">
      <div className="d-flex justify-content-between mb-3">
        <div className="d-flex align-items-center">
          <h2 className="mb-0">{translate(language, "PAGES.COHORT.USERS.TITLE")}</h2>
          <h5 className="ms-2 me-3 mb-0">( {!loading && cohortUsers.length} )</h5>
          <DstTableSearch
            data={preProcessedData}
            searchableColumns={["lastName", "firstName", "email", "active"]}
            onSearch={setFilteredData}
          />
        </div>
        <div className="d-flex ms-3">
          {usersSelected.length > 0 ? (
            <div className="d-flex flex-wrap align-items-center gap-3" id="cohort-users-button">
              <PermissionComponent neededPermission={[{ methods: ["PUT"], route: "/cohorts/*/users" }]}>
                <React.Fragment>
                  <DstButton
                    btnClass="xxs-bold d-flex align-items-center"
                    btnImageBefore={<span className="dst-icon-users-right d-flex m-regular" />}
                    clickFunction={() => setOpenAddUsersToOtherCohortModal(true)}
                    tooltip={translate(language, "PAGES.COHORT.USERS.BUTTONS.ADD_TO_OTHER_COHORT")}
                    value=""
                  />
                  <div className="border-start height-30px"></div>
                </React.Fragment>
              </PermissionComponent>
              <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/cohorts/*/users/flags/batch" }]}>
                <React.Fragment>
                  <DstButton
                    btnImageBefore={<span className="dst-icon-award-03 d-flex m-regular" />}
                    value=""
                    tooltip={translate(language, "PAGES.COHORT.USERS.BUTTONS.ADD_FLAGS_TO_USERS")}
                    btnClass="xxs-bold d-flex align-items-center"
                    clickFunction={() => setOpenAddFlagsToUsersModal(true)}
                  />
                  <div className="border-start height-30px"></div>
                </React.Fragment>
              </PermissionComponent>
              <PermissionComponent neededPermission={[{ methods: ["PUT"], route: "/cohorts/*/users" }]}>
                <React.Fragment>
                  <DstButton
                    btnClass="xxs-bold d-flex align-items-center"
                    btnImageBefore={<span className="dst-icon-user-minus-02 d-flex m-regular" />}
                    variant="danger"
                    clickFunction={() => setShowConfirmRemoveUsers(true)}
                    tooltip={translate(language, "PAGES.COHORT.USERS.BUTTONS.REMOVE_USERS")}
                    value=""
                  />
                  <div className="border-start height-30px"></div>
                </React.Fragment>
              </PermissionComponent>
              <PermissionComponent
                neededPermission={[{ methods: ["POST"], route: "/cohorts/*/users/evaluations/*/attempts" }]}
              >
                <React.Fragment>
                  <DstButton
                    btnClass="xxs-bold d-flex align-items-center"
                    btnImageBefore={<span className="dst-icon-pen-tool-plus d-flex m-regular" />}
                    clickFunction={() => setOpenEvaluationInvitationModal(true)}
                    tooltip={translate(language, "PAGES.COHORT.USERS.BUTTONS.EVALUATION_INVITE")}
                    value=""
                  />
                  <div className="border-start height-30px"></div>
                </React.Fragment>
              </PermissionComponent>
              <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/cohorts/*/users/progress/batch" }]}>
                <React.Fragment>
                  <DstButton
                    btnClass="xxs-bold d-flex align-items-center"
                    btnImageBefore={<span className="dst-icon-file-download-02 d-flex m-regular" />}
                    clickFunction={() => setOpenDownloadProgressReportModal(true)}
                    tooltip={translate(language, "PAGES.COHORT.USERS.BUTTONS.PROGRESS_REPORT")}
                    value=""
                  />
                  <div className="border-start height-30px"></div>
                </React.Fragment>
              </PermissionComponent>
              <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/cohorts/*/users/modules/batch" }]}>
                <DstButton
                  btnClass="xxs-bold d-flex align-items-center"
                  btnImageBefore={<span className="dst-icon-plus-circle d-flex m-regular" />}
                  tooltip={translate(language, "PAGES.COHORT.USERS.BUTTONS.ADD_MODULE")}
                  clickFunction={() => setOpenAddModuleModal(true)}
                  value=""
                />
              </PermissionComponent>
              {isCohortPartner && (
                <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/cohorts/*/partners/diplomas" }]}>
                  <div className="border-start height-30px"></div>
                  <DstButton
                    btnClass="xxs-bold d-flex align-items-center"
                    btnImageBefore={<span className="dst-icon-certificate-01 d-flex m-regular" />}
                    clickFunction={() => setOpenPartnerDiplomaModal(true)}
                    tooltip={translate(language, "PAGES.COHORT.USERS.BUTTONS.PARTNER_DIPLOMA_GENERATE")}
                    value=""
                  />
                </PermissionComponent>
              )}
            </div>
          ) : (
            <React.Fragment>
              <PermissionComponent neededPermission={[{ methods: ["PUT"], route: "/cohorts/*/users" }]}>
                <DstButton
                  btnImageBefore={<span className="dst-icon-user-plus-02 me-2" />}
                  value={translate(language, "PAGES.COHORT.USERS.BUTTONS.ADD_USERS")}
                  variant="primary"
                  btnClass="me-2 d-flex align-items-center"
                  clickFunction={() => setOpenAddUsersModal(true)}
                />
              </PermissionComponent>
              <DstButton
                btnImageBefore={<span className="dst-icon-copy-03 me-2" />}
                value={translate(language, "PAGES.COHORT.USERS.COPY_ALL_EMAILS")}
                variant="light"
                btnClass="me-2 d-flex align-items-center"
                clickFunction={_copyAllEmails}
              />
              <PermissionComponent neededPermission={[{ methods: ["GET"], route: "/cohorts/*/users/logtime/weekly" }]}>
                <DstButton
                  btnImageBefore={<span className="dst-icon-download-03 me-2" />}
                  value={translate(language, "PAGES.COHORT.USERS.FETCH_LOGTIME")}
                  variant="light"
                  loading={loadingLogtime}
                  btnClass="me-2 d-flex align-items-center"
                  clickFunction={() =>
                    fetchUsersLogtime(
                      cohortId,
                      cohortUsers,
                      setCohortUsers,
                      language,
                      setLoadingLogtime,
                      setFilteredData
                    )
                  }
                />
              </PermissionComponent>
              <OverlayTrigger
                trigger="click"
                rootClose
                placement="bottom"
                overlay={
                  <Popover id="color-code-popover">
                    <Popover.Body className="s-bold">
                      <p className="d-flex align-items-center">
                        <div className="bg-primary-subtle color-circle me-2"></div> VIP
                      </p>
                      <p className="d-flex align-items-center mb-0">
                        <div className="bg-warning-subtle color-circle me-2"></div> Complicated
                      </p>
                    </Popover.Body>
                  </Popover>
                }
              >
                <div>
                  <DstButton
                    variant="light"
                    btnClass="h-100 d-flex align-items-center"
                    value={translate(language, "PAGES.COHORT.USERS.COLOR_CODE")}
                    btnImageBefore={<span className="dst-icon-help-circle me-2" />}
                  />
                </div>
              </OverlayTrigger>
            </React.Fragment>
          )}
        </div>
      </div>
      <DstTable
        data={filteredData}
        columns={columns}
        onRowClick={(event, item) => completeNavigate(event, navigate, `/user/${item.id}`)}
        classLine={(elem) => styleUserRow(elem)}
        striped={false}
        selectable
        selectedItems={usersSelected}
        onRowSelect={(selectedItems) => setUsersSelected(selectedItems)}
      />
      <CohortUsersPopup
        language={language}
        openAddUsersModal={openAddUsersModal}
        setOpenAddUsersModal={setOpenAddUsersModal}
        actualUserIds={cohortUsers.map((user) => user.id)}
        cohortId={cohortId}
        setLoading={setLoading}
        setCohortUsers={setCohortUsers}
        onUsersAdded={onUsersAdded}
      />
      <AddUsersToOtherCohortPopup
        language={language}
        openAddUsersToOtherCohortModal={openAddUsersToOtherCohortModal}
        setOpenAddUsersToOtherCohortModal={setOpenAddUsersToOtherCohortModal}
        usersToAddToCohort={usersSelected.map((user) => user.id)}
        setUsersSelected={setUsersSelected}
      />
      <CohortUsersFlagsPopup
        language={language}
        cohortId={cohortId}
        openPopup={openAddFlagsToUsersModal}
        setOpenPopup={setOpenAddFlagsToUsersModal}
        userIds={usersSelected.map((user) => user.id)}
        setUsersSelected={setUsersSelected}
        setLoading={setLoading}
        setCohortUsers={setCohortUsers}
      />
      <CohortPartnerDiplomaPopup
        language={language}
        cohortId={cohortId}
        userIds={usersSelected.map((user) => user.id)}
        openPartnerDiplomaModal={openPartnerDiplomaModal}
        setOpenPartnerDiplomaModal={setOpenPartnerDiplomaModal}
      />
      <CohortUserEvaluationAttemptsPopup
        language={language}
        cohortId={cohortId}
        userIds={usersSelected.map((user) => user.id)}
        openPopup={openEvaluationInvitationModal}
        setOpenPopup={setOpenEvaluationInvitationModal}
      />
      <CohortUsersProgressReportPopup
        language={language}
        cohortId={cohortId}
        userIds={usersSelected.map((user) => user.id)}
        openPopup={openDownloadProgressReportModal}
        setOpenPopup={setOpenDownloadProgressReportModal}
      />
      <AddModulesPopup
        show={openAddModuleModal}
        closePopup={() => setOpenAddModuleModal(false)}
        cohortId={cohortId}
        usersSelected={usersSelected}
        language={language}
        setCohortUsers={setCohortUsers}
        setUsersSelected={setUsersSelected}
      />
      <ToastContainer position="top-end" className="p-3">
        <Toast bg="success" onClose={() => setShowCopyToast(false)} show={showCopyToast} delay={2000} autohide>
          <Toast.Body className="text-white">{translate(language, "PAGES.COHORT.USERS.COPY_SUCCESS_MSG")}</Toast.Body>
        </Toast>
      </ToastContainer>
      <DstConfirm
        show={showConfirmRemoveUsers}
        onHide={() => setShowConfirmRemoveUsers(false)}
        onConfirm={(note?: string) => {
          setShowConfirmRemoveUsers(false);
          setLoading(true);
          updateUsers(
            cohortUsers
              .filter((user) => !usersSelected.map((item) => item.id).includes(user.id))
              .map((user) => user.id),
            false,
            cohortId,
            language,
            () => {
              fetchUsers(cohortId, setCohortUsers, language, () => {
                setLoading(false);
                setUsersSelected([]);
                onUsersRemoved?.();
              });
            },
            undefined,
            note
          );
        }}
        title={translate(language, "PAGES.COHORT.USERS.REMOVE_CONFIRM.TITLE")}
        messageExplain={translate(language, "PAGES.COHORT.USERS.REMOVE_CONFIRM.EXPLAIN")}
        messageConfirm={translate(language, "PAGES.COHORT.USERS.REMOVE_CONFIRM.MESSAGE")}
        confirmTextButton={translate(language, "PAGES.COHORT.USERS.REMOVE_CONFIRM.BUTTON")}
        language={language}
        noteItems={_REMOVE_USER_NOTE_KEYS.map(({ id, key }) => ({
          id,
          label: translate(language, key),
        }))}
        noteItemsPlaceholder={translate(language, "PAGES.COHORT.USERS.REMOVE_CONFIRM.NOTE_ITEMS_PLACEHOLDER")}
        noteLabel={translate(language, "PAGES.COHORT.USERS.REMOVE_CONFIRM.NOTE_LABEL")}
        notePlaceholder={translate(language, "PAGES.COHORT.USERS.REMOVE_CONFIRM.NOTE_PLACEHOLDER")}
        showNoteInput
      />
    </div>
  );
};

export default CohortUsers;
